import { TrashSimple } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation, IButtonProps } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useProjectUserActions } from 'projects/hooks';
import { IProjectUser } from 'projects/types';
import { Color, Variant } from 'theme/enums';

interface IProps extends IButtonProps {
  projectId: number;
  users: IProjectUser[];
  buttonElement?: ReactElement;
  notificationPosition?: NotificationPosition;
}

export const RemoveUserButton = ({
  projectId,
  users,
  buttonElement,
  notificationPosition = NotificationPosition.FORM,
  ...rest
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { removeProjectUsers } = useProjectUserActions(notificationPosition);

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const { success } = await removeProjectUsers({
      projectCorporateUsers: users.map((user) => ({
        projectId,
        corporateUserId: user.id,
      })),
    });

    if (!success) {
      return;
    }
  }, [removeProjectUsers, users, projectId]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={rest?.text || t('buttonLabelRemoveProjectUser')}
        variant={Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<TrashSimple />}
        onClick={() => setIsConfirmationOpen(true)}
        {...rest}
      />
    );
  }, [buttonElement, t, rest]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveProjectUsers', {
          count: users.length,
        })}
        message={t('messageConfirmRemoveProjectUsers', {
          count: users.length,
          userName: users[0].name,
        })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
